import type { HingeSide } from '../drawing/door';
import type { DoorCatalogue, DoorRange } from '../data/doorCatalogue';
import {
  HANDLE_INSET,
  MAX_HINGES,
  EDGE_STD,
  hingedLength,
  isFlap,
  newPiece,
  refitHandle,
  refitHinge,
  standardCount,
  standardHandle,
  standardHinge,
  standardPositions,
  type HandleDrillType,
  type HandleOrientation,
  type HandleProduct,
  type Piece,
  type PieceKind,
} from './drilling';

/**
 * The door builder's state and every change a shopper can make, as a pure
 * reducer. Ported from the mockup's event handlers; each action says which
 * mockup behaviour it keeps.
 */
export type DoorsFor = 'kitchen' | 'wardrobe';
export type DoorView = 'front' | 'back' | 'all';

export interface DoorOrderState {
  mode: DoorsFor;
  rangeId: string;
  /** A catalogue colour id, or `ral`. */
  colourId: string;
  /** The colour to paint when `colourId` is `ral`, as the shopper typed it. */
  paintColour: string;
  /** The shopper's own handles, which decide the default drilling. The shop supplies none yet. */
  handle: HandleProduct;
  softClose: boolean;
  view: DoorView;
  active: number;
  pieces: Piece[];
}

export type HandleMove = 'centre-x' | 'top' | 'middle' | 'bottom' | 'standard';

export type DoorOrderAction =
  | { type: 'mode'; mode: DoorsFor }
  | { type: 'range'; rangeId: string }
  | { type: 'colour'; colourId: string }
  | { type: 'paintColour'; value: string }
  | { type: 'handleProduct'; handle: HandleProduct }
  | { type: 'softClose'; on: boolean }
  | { type: 'view'; view: DoorView }
  | { type: 'select'; index: number }
  | { type: 'step'; by: -1 | 1 }
  | { type: 'add'; kind?: PieceKind; h?: number; w?: number; side?: HingeSide }
  | { type: 'remove'; index: number }
  | { type: 'kind'; index: number; kind: PieceKind }
  | { type: 'size'; index: number; field: 'h' | 'w'; value: number }
  | { type: 'qty'; index: number; value: number }
  | { type: 'hingeSide'; index: number; side: HingeSide }
  | { type: 'hingeCount'; index: number; count: number }
  | { type: 'hingeEdge'; index: number; edge: number }
  | { type: 'hingePosition'; index: number; at: number; value: number }
  | { type: 'hingeReset'; index: number }
  | { type: 'handleType'; index: number; drill: HandleDrillType }
  | { type: 'handleCentres'; index: number; centres: number }
  | { type: 'handleOrient'; index: number; orient: HandleOrientation }
  | { type: 'handleAt'; index: number; field: 'x' | 'y'; value: number }
  | { type: 'handleMove'; index: number; move: HandleMove };

const whole = (value: number) => (Number.isFinite(value) ? Math.round(value) : 0);

/**
 * The ranges offered for kitchen or wardrobe doors: only those the shop makes doors in. A range
 * published with end panels alone (Belgravia, whose in-frame doorsets cannot be cut down) would
 * otherwise be offered and then refused by the server on the first door.
 */
export function rangesFor(catalogue: DoorCatalogue, mode: DoorsFor): DoorRange[] {
  return catalogue.ranges().filter((r) => (mode === 'wardrobe' ? Boolean(r.wardrobe) : !r.wardrobe) && Boolean(r.sizes.door));
}

export function starterPieces(mode: DoorsFor, handle: HandleProduct): Piece[] {
  return mode === 'wardrobe'
    ? [newPiece('door', 2100, 496, 'left', 2, handle), newPiece('door', 2100, 496, 'right', 2, handle), newPiece('door', 400, 992, 'top', 1, handle)]
    : [newPiece('door', 715, 497, 'left', 2, handle), newPiece('drawer', 140, 497, 'none', 1, handle)];
}

export function offers(range: DoorRange, colourId: string | undefined): boolean {
  return Boolean(colourId) && (range.colours.includes(colourId as string) || (colourId === 'ral' && Boolean(range.ral)));
}

/** A range's first colour; a range sold only painted to order starts on that. */
export function firstColour(range: DoorRange): string {
  return range.colours[0] ?? 'ral';
}

/** The ranges a mode can offer, whether or not the catalogue has any. */
export function hasRanges(catalogue: DoorCatalogue, mode: DoorsFor): boolean {
  return rangesFor(catalogue, mode).length > 0;
}

export function initialOrder(
  catalogue: DoorCatalogue,
  preset: { mode?: DoorsFor; rangeId?: string; colourId?: string } = {},
): DoorOrderState {
  const mode = preset.mode ?? 'kitchen';
  const ranges = rangesFor(catalogue, mode);
  const range = ranges.find((r) => r.id === preset.rangeId) ?? ranges[0];
  if (!range) throw new Error(`The door catalogue has no ${mode} ranges.`);
  const colourId = offers(range, preset.colourId) ? (preset.colourId as string) : firstColour(range);
  const handle: HandleProduct = range.noHandle ? 'none' : 'bar';
  return {
    mode,
    rangeId: range.id,
    colourId,
    paintColour: '',
    handle,
    softClose: false,
    view: 'front',
    active: 0,
    pieces: starterPieces(mode, handle),
  };
}

function withPiece(state: DoorOrderState, index: number, change: (piece: Piece) => Piece): DoorOrderState {
  const piece = state.pieces[index];
  if (!piece) return state;
  const pieces = state.pieces.slice();
  pieces[index] = change(piece);
  return { ...state, pieces, active: index };
}

function selectRange(state: DoorOrderState, catalogue: DoorCatalogue, rangeId: string): DoorOrderState {
  const range = catalogue.ranges().find((r) => r.id === rangeId);
  if (!range) return state;
  // A rail-system range takes no handles. Leaving one restores bar handles; a
  // shopper's own "none" on an ordinary range is kept.
  const previous = catalogue.ranges().find((r) => r.id === state.rangeId);
  const handle: HandleProduct = range.noHandle ? 'none' : previous?.noHandle ? 'bar' : state.handle;
  const colourId = offers(range, state.colourId) ? state.colourId : firstColour(range);
  const pieces = state.pieces.map((p) =>
    p.handle.custom ? p : { ...p, handle: standardHandle(p, handle, range.noHandle ? { type: 'none' } : undefined) },
  );
  return { ...state, rangeId: range.id, colourId, handle, pieces };
}

export function doorOrderReducer(catalogue: DoorCatalogue) {
  return function reduce(state: DoorOrderState, action: DoorOrderAction): DoorOrderState {
    switch (action.type) {
      case 'mode': {
        if (action.mode === state.mode || !hasRanges(catalogue, action.mode)) return state;
        const next = initialOrder(catalogue, { mode: action.mode });
        return { ...next, view: state.view };
      }
      case 'range':
        return selectRange(state, catalogue, action.rangeId);
      case 'colour': {
        const range = catalogue.ranges().find((r) => r.id === state.rangeId);
        return range && offers(range, action.colourId) ? { ...state, colourId: action.colourId } : state;
      }
      case 'paintColour':
        return { ...state, paintColour: action.value.slice(0, 60) };
      case 'handleProduct': {
        const range = catalogue.ranges().find((r) => r.id === state.rangeId);
        if (range?.noHandle) return state;
        const handle = action.handle;
        return {
          ...state,
          handle,
          pieces: state.pieces.map((p) => (p.handle.custom ? p : { ...p, handle: standardHandle(p, handle) })),
        };
      }
      case 'softClose':
        return { ...state, softClose: action.on };
      case 'view':
        return { ...state, view: action.view };
      case 'select':
        return action.index >= 0 && action.index < state.pieces.length ? { ...state, active: action.index } : state;
      case 'step': {
        const active = Math.max(0, Math.min(state.pieces.length - 1, state.active + action.by));
        return { ...state, active };
      }
      case 'add': {
        const kind = action.kind ?? 'door';
        const piece = newPiece(kind, action.h ?? 715, action.w ?? 397, action.side ?? (kind === 'door' ? 'left' : 'none'), 1, state.handle);
        return { ...state, pieces: [...state.pieces, piece], active: state.pieces.length };
      }
      case 'remove': {
        if (state.pieces.length <= 1 || !state.pieces[action.index]) return state;
        const pieces = state.pieces.filter((_, i) => i !== action.index);
        return { ...state, pieces, active: Math.min(state.active, pieces.length - 1) };
      }
      case 'kind':
        return withPiece(state, action.index, (p) => {
          // A drawer front or end panel has no hinges; a door keeps its side.
          const hinge = action.kind !== 'door' && p.hinge.side !== 'none' ? standardHinge('none', 0) : p.hinge;
          const next: Piece = { ...p, kind: action.kind, hinge };
          return { ...next, handle: next.handle.custom ? next.handle : standardHandle(next, state.handle) };
        });
      case 'size':
        return withPiece(state, action.index, (p) => {
          const next: Piece = { ...p, [action.field]: Math.max(0, whole(action.value)) };
          const refitted: Piece = { ...next, hinge: refitHinge(next) };
          return { ...refitted, handle: refitHandle(refitted, state.handle) };
        });
      case 'qty':
        return withPiece(state, action.index, (p) => ({ ...p, qty: Math.max(1, whole(action.value)) }));
      case 'hingeSide':
        return withPiece(state, action.index, (p) => {
          // Choosing a hinge side makes a drawer front or panel a door, as in the mockup.
          const kind: PieceKind = action.side !== 'none' ? 'door' : p.kind;
          const next: Piece = { ...p, kind, hinge: standardHinge(action.side, hingedLength(p, action.side)) };
          return { ...next, handle: refitHandle(next, state.handle) };
        });
      case 'hingeCount':
        return withPiece(state, action.index, (p) => {
          if (p.hinge.side === 'none') return p;
          const count = Math.max(1, Math.min(MAX_HINGES, whole(action.count)));
          const length = hingedLength(p, p.hinge.side);
          return { ...p, hinge: { ...p.hinge, count, positions: standardPositions(length, count), custom: count !== standardCount(length) } };
        });
      case 'hingeEdge':
        return withPiece(state, action.index, (p) =>
          p.hinge.side === 'none' ? p : { ...p, hinge: { ...p.hinge, edge: Number.isFinite(action.edge) ? Math.round(action.edge * 2) / 2 : EDGE_STD, custom: true } },
        );
      case 'hingePosition':
        return withPiece(state, action.index, (p) => {
          if (!(action.at >= 0 && action.at < p.hinge.positions.length)) return p;
          const positions = p.hinge.positions.slice();
          positions[action.at] = whole(action.value);
          return { ...p, hinge: { ...p.hinge, positions, custom: true } };
        });
      case 'hingeReset':
        return withPiece(state, action.index, (p) => ({ ...p, hinge: refitHinge({ ...p, hinge: { ...p.hinge, edge: EDGE_STD, custom: false } }) }));
      case 'handleType':
        return withPiece(state, action.index, (p) => {
          const handle = standardHandle(p, state.handle, { type: action.drill, centres: p.handle.centres || 128 });
          return { ...p, handle: { ...handle, custom: action.drill !== standardHandle(p, state.handle).type } };
        });
      case 'handleCentres':
        return withPiece(state, action.index, (p) => ({ ...p, handle: { ...p.handle, centres: whole(action.centres), custom: true } }));
      case 'handleOrient':
        return withPiece(state, action.index, (p) => {
          // Turning a pair keeps both holes on the door: pull the centre in from the edges it now spans.
          const d = p.handle;
          const c = d.type === 'double' ? d.centres / 2 : 0;
          const x = action.orient === 'horizontal' ? Math.round(Math.min(Math.max(d.x, c + HANDLE_INSET), p.w - c - HANDLE_INSET)) : d.x;
          const y = action.orient === 'vertical' ? Math.round(Math.min(Math.max(d.y, c + HANDLE_INSET), p.h - c - HANDLE_INSET)) : d.y;
          return { ...p, handle: { ...d, orient: action.orient, x, y, custom: true } };
        });
      case 'handleAt':
        return withPiece(state, action.index, (p) => ({ ...p, handle: { ...p.handle, [action.field]: whole(action.value), custom: true } }));
      case 'handleMove':
        return withPiece(state, action.index, (p) => {
          const d = p.handle;
          if (action.move === 'standard') return { ...p, handle: standardHandle(p, state.handle, { type: d.type, centres: d.centres }) };
          const reach = d.type === 'double' && d.orient === 'vertical' ? d.centres / 2 : 0;
          const moved = { ...d, custom: true };
          if (action.move === 'centre-x') moved.x = Math.round(p.w / 2);
          if (action.move === 'top') moved.y = Math.round(reach + 50);
          if (action.move === 'middle') moved.y = Math.round(p.h / 2);
          if (action.move === 'bottom') moved.y = Math.round(p.h - reach - 50);
          return { ...p, handle: moved };
        });
      default:
        return state;
    }
  };
}

/** A human line describing a piece's hinges, for the panel header. */
export function hingeSummary(piece: Piece): string {
  const { side, count } = piece.hinge;
  if (side === 'none') return 'no hinge drilling';
  const hung = side === 'top' ? ' (top-hung)' : side === 'bottom' ? ' (bottom-hung)' : '';
  return `${count} hinges, ${side}${hung}`;
}

export { isFlap };
